/**
 * PriceSupportFAB — Voiture Price flottante draggable + chat WhatsApp
 * ────────────────────────────────────────────────────────────────────
 * S'affiche en fixed sur les dashboards passager/covoit. L'utilisateur
 * peut la déplacer en drag pour ne pas gêner l'UI. Au tap simple, elle
 * ouvre un modal chat avec l'équipe Price. À la fermeture, elle revient
 * à sa position sauvegardée dans localStorage.
 *
 * Polling : quand le chat est FERMÉ, poll /price/support/unread toutes
 * les 15 s pour afficher le badge non-lu sur la voiture. Quand le chat
 * est OUVERT, poll /price/support/messages toutes les 4 s.
 */
import React, { useEffect, useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";
import SupportChat from "@/components/price/SupportChat";

const STORAGE_KEY = "price_support_fab_pos";
const FAB_SIZE = 60;

function loadSavedPos() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch (_) {/*noop*/}
  // Default : bas droite
  const w = typeof window !== "undefined" ? window.innerWidth : 400;
  const h = typeof window !== "undefined" ? window.innerHeight : 800;
  return { x: Math.max(12, w - FAB_SIZE - 16), y: Math.max(12, h - FAB_SIZE - 120) };
}

function savePos(pos) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(pos)); } catch (_) {/*noop*/}
}

export default function PriceSupportFAB() {
  const [pos, setPos] = useState(loadSavedPos);
  const [dragging, setDragging] = useState(false);
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const dragStartRef = useRef(null);
  const movedRef = useRef(false);

  // Polling badge
  const refreshUnread = useCallback(async () => {
    try {
      const r = await api.get("/price/support/unread");
      setUnread(Number(r.data?.unread || 0));
    } catch (_) {/*noop*/}
  }, []);

  useEffect(() => {
    if (open) return;
    refreshUnread();
    const t = setInterval(refreshUnread, 15000);
    return () => clearInterval(t);
  }, [open, refreshUnread]);

  // Drag handlers — pointer events pour mobile + desktop
  const onDown = (e) => {
    e.preventDefault();
    const p = e.touches ? e.touches[0] : e;
    dragStartRef.current = { px: p.clientX, py: p.clientY, x: pos.x, y: pos.y, t: Date.now() };
    movedRef.current = false;
    setDragging(true);
  };

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e) => {
      const p = e.touches ? e.touches[0] : e;
      const s = dragStartRef.current;
      if (!s) return;
      const dx = p.clientX - s.px;
      const dy = p.clientY - s.py;
      if (Math.abs(dx) > 5 || Math.abs(dy) > 5) movedRef.current = true;
      const nx = Math.max(4, Math.min(window.innerWidth - FAB_SIZE - 4, s.x + dx));
      const ny = Math.max(4, Math.min(window.innerHeight - FAB_SIZE - 4, s.y + dy));
      setPos({ x: nx, y: ny });
    };
    const onUp = () => {
      setDragging(false);
      setPos((p) => { savePos(p); return p; });
      // Si pas de mouvement → tap → open chat
      const s = dragStartRef.current;
      if (s && !movedRef.current && Date.now() - s.t < 500) {
        setOpen(true);
      }
      dragStartRef.current = null;
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
  }, [dragging]);

  // Repose the FAB on window resize so it never goes off-screen.
  useEffect(() => {
    const onResize = () => setPos((p) => {
      const nx = Math.max(4, Math.min(window.innerWidth - FAB_SIZE - 4, p.x));
      const ny = Math.max(4, Math.min(window.innerHeight - FAB_SIZE - 4, p.y));
      return { x: nx, y: ny };
    });
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Ouverture depuis l'onglet « Messages » de la barre du bas.
  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener("price:open-support", onOpen);
    return () => window.removeEventListener("price:open-support", onOpen);
  }, []);

  return (
    <>
      {/* Voiture Price flottante */}
      <button
        type="button"
        aria-label="Contacter Price"
        data-testid="price-support-fab"
        onPointerDown={onDown}
        style={{
          position: "fixed",
          left: pos.x,
          top: pos.y,
          width: FAB_SIZE,
          height: FAB_SIZE,
          zIndex: 80,
          touchAction: "none",
          userSelect: "none",
          WebkitUserSelect: "none",
          WebkitTouchCallout: "none",  // iOS : bloque copier/coller
          WebkitTapHighlightColor: "transparent",  // iOS : flash bleu
          cursor: dragging ? "grabbing" : "grab",
        }}
        className="rounded-2xl bg-primary text-primary-foreground grid place-items-center border-2 border-black shadow-[0_5px_0_0_#000] active:translate-y-[2px] active:shadow-[0_3px_0_0_#000] transition-shadow"
      >
        {/* Cartoon Price car SVG */}
        <svg viewBox="0 0 60 40" width={36} height={36} aria-hidden>
          <g stroke="#000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M6 26 L10 16 Q11 13 14 13 L46 13 Q49 13 50 16 L54 26 L54 32 Q54 34 52 34 L48 34 Q46 34 46 32 L46 30 L14 30 L14 32 Q14 34 12 34 L8 34 Q6 34 6 32 Z" fill="#fff" />
            <path d="M13 22 L47 22" fill="none" />
            <circle cx="15" cy="30" r="3.5" fill="#000" />
            <circle cx="45" cy="30" r="3.5" fill="#000" />
            <path d="M18 15 L22 21 L38 21 L42 15" fill="#FFD9DA" />
          </g>
        </svg>
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-5 h-5 rounded-full bg-white text-primary text-[10px] font-black grid place-items-center px-1 border-2 border-black">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <UserSupportChatWindow onClose={() => { setOpen(false); refreshUnread(); }} />
      )}
    </>
  );
}

function UserSupportChatWindow({ onClose }) {
  return (
    <SupportChat
      title="L'équipe Price"
      loadUrl="/price/support/messages"
      sendUrl="/price/support/messages"
      typingUrl="/price/support/typing"
      mine="user"
      onClose={onClose}
      testPrefix="user-support"
      emptyText="Une question ? Un souci ? Écris-nous, on te répond au plus vite !"
    />
  );
}
